import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Network, ChevronUp, ChevronDown } from 'lucide-react';
import { Btn, Modal, FormField, Badge, inputCls } from '../ui';
import { useToast } from '../../contexts/NotificationContext';
import { getOrgRoles, createOrgRole, updateOrgRole, deleteOrgRole } from '../../api';

const EMPTY = { name: '', code: '', parentId: '', position: 0, allowsContinuiteDir: false, isActive: true };

function slugify(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

export default function HROrgRoleManagement() {
  const toast = useToast();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [codeManual, setCodeManual] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getOrgRoles();
      setRoles(data);
    } catch {
      toast.error('Erreur chargement des rôles');
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, position: roles.length + 1 });
    setCodeManual(false);
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      code: r.code,
      parentId: r.parentId || '',
      position: r.position,
      allowsContinuiteDir: r.allowsContinuiteDir,
      isActive: r.isActive,
    });
    setCodeManual(true);
    setShowModal(true);
  };

  const handleNameChange = (val) => {
    setForm(f => ({
      ...f,
      name: val,
      code: codeManual ? f.code : slugify(val),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Le nom est obligatoire'); return; }
    if (!form.code.trim()) { toast.error('Le code est obligatoire'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        parentId: form.parentId || null,
        position: parseInt(form.position) || 0,
        allowsContinuiteDir: form.allowsContinuiteDir,
        isActive: form.isActive,
      };
      if (editing) {
        await updateOrgRole(editing.id, payload);
        toast.success('Rôle mis à jour');
      } else {
        await createOrgRole(payload);
        toast.success('Rôle ajouté');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur enregistrement');
    }
    setSaving(false);
  };

  const handleDelete = async (r) => {
    try {
      await deleteOrgRole(r.id);
      toast.success('Rôle supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression');
    }
    setDeleteConfirm(null);
  };

  const movePosition = async (r, dir) => {
    try {
      await updateOrgRole(r.id, { position: r.position + dir });
      load();
    } catch {
      toast.error('Erreur déplacement');
    }
  };

  // Rôles disponibles comme parents (exclut le rôle en cours d'édition)
  const parentOptions = roles.filter(r => !editing || r.id !== editing.id);

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Network size={18} className="text-primary" />
          <h2 className="text-lg font-semibold text-gray-800">Rôles de l'organigramme</h2>
        </div>
        <Btn variant="primary" size="sm" onClick={openCreate}>
          <Plus size={14} /> Ajouter
        </Btn>
      </div>

      <p className="text-sm text-gray-500">
        Ces rôles définissent la hiérarchie de l'organigramme. Le <strong>parent</strong> détermine
        où le rôle apparaît dans l'arbre. Les rôles avec <strong>continuité de direction</strong> peuvent
        être désignés comme remplaçants du directeur.
      </p>

      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Chargement…</div>
      ) : roles.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400">Aucun rôle configuré.</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-8">Ord.</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Nom</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Code</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Parent</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Cont. direction</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Statut</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              <RoleTreeRows
                parentId={null}
                allRoles={roles}
                depth={0}
                onEdit={openEdit}
                onDelete={setDeleteConfirm}
                onMove={movePosition}
              />
            </tbody>
          </table>
        </div>
      )}

      {/* Modal création / édition */}
      {showModal && (
        <Modal
          title={editing ? 'Modifier le rôle' : 'Nouveau rôle'}
          size="sm"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <Btn variant="secondary" size="sm" onClick={() => setShowModal(false)}>Annuler</Btn>
              <Btn variant="primary" size="sm" loading={saving} onClick={handleSave}>Enregistrer</Btn>
            </>
          }
        >
          <div className="space-y-4">
            <FormField label="Nom" required>
              <input
                className={inputCls}
                value={form.name}
                onChange={e => handleNameChange(e.target.value)}
                placeholder="Ex : Directeur adjoint"
              />
            </FormField>

            <FormField label="Code" required>
              <input
                className={inputCls}
                value={form.code}
                onChange={e => { setCodeManual(true); setForm(f => ({ ...f, code: slugify(e.target.value) })); }}
                placeholder="Ex : directeur-adjoint"
              />
              <p className="text-xs text-gray-400 mt-1">Identifiant technique (auto-généré). Modifier uniquement si nécessaire.</p>
            </FormField>

            <FormField label="Rôle parent">
              <select
                className={inputCls}
                value={form.parentId}
                onChange={e => setForm(f => ({ ...f, parentId: e.target.value }))}
              >
                <option value="">— Racine (pas de parent) —</option>
                {parentOptions.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Détermine la position dans l'arbre hiérarchique.</p>
            </FormField>

            <FormField label="Continuité de direction">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.allowsContinuiteDir}
                  onChange={e => setForm(f => ({ ...f, allowsContinuiteDir: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">Les employés avec ce rôle peuvent être désignés comme continuité de direction</span>
              </label>
            </FormField>

            <FormField label="Statut">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">Actif (visible dans les sélecteurs)</span>
              </label>
            </FormField>
          </div>
        </Modal>
      )}

      {/* Modal confirmation suppression */}
      {deleteConfirm && (
        <Modal
          title="Supprimer le rôle"
          size="sm"
          onClose={() => setDeleteConfirm(null)}
          footer={
            <>
              <Btn variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Annuler</Btn>
              <Btn variant="danger" size="sm" onClick={() => handleDelete(deleteConfirm)}>Supprimer</Btn>
            </>
          }
        >
          <p className="text-sm text-gray-700">
            Supprimer <strong>{deleteConfirm.name}</strong> ?
            La suppression sera bloquée si des employés ont ce rôle ou si des sous-rôles en dépendent.
            Désactivez-le plutôt dans ce cas.
          </p>
        </Modal>
      )}
    </div>
  );
}

// Rendu récursif de l'arbre — gère tous les niveaux de profondeur
function RoleTreeRows({ parentId, allRoles, depth, onEdit, onDelete, onMove }) {
  const children = allRoles
    .filter(r => (r.parentId ?? null) === parentId)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));

  return children.map((role, i) => (
    <React.Fragment key={role.id}>
      <RoleRow
        role={role}
        indent={depth}
        isFirst={i === 0}
        isLast={i === children.length - 1}
        onEdit={onEdit}
        onDelete={onDelete}
        onMove={onMove}
      />
      <RoleTreeRows
        parentId={role.id}
        allRoles={allRoles}
        depth={depth + 1}
        onEdit={onEdit}
        onDelete={onDelete}
        onMove={onMove}
      />
    </React.Fragment>
  ));
}

function RoleRow({ role, isFirst, isLast, indent, onEdit, onDelete, onMove }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-gray-400 text-xs">
        <div className="flex flex-col gap-0.5">
          <button onClick={() => onMove(role, -1)} disabled={isFirst} className="disabled:opacity-30 hover:text-primary"><ChevronUp size={12} /></button>
          <button onClick={() => onMove(role, 1)} disabled={isLast} className="disabled:opacity-30 hover:text-primary"><ChevronDown size={12} /></button>
        </div>
      </td>
      <td className="px-4 py-3 font-medium text-gray-800">
        <span style={{ paddingLeft: indent * 20 }} className="flex items-center gap-1">
          {indent > 0 && <span className="text-gray-300 text-xs select-none">└</span>}
          {role.name}
        </span>
      </td>
      <td className="px-4 py-3 text-gray-400 font-mono text-xs">{role.code}</td>
      <td className="px-4 py-3 text-gray-500 text-xs">
        {role.parent?.name ?? <span className="text-gray-300">—</span>}
      </td>
      <td className="px-4 py-3">
        {role.allowsContinuiteDir
          ? <Badge color="bg-amber-100 text-amber-700">Oui</Badge>
          : <span className="text-gray-300 text-xs">—</span>}
      </td>
      <td className="px-4 py-3">
        {role.isActive
          ? <Badge color="bg-green-100 text-green-700">Actif</Badge>
          : <Badge color="bg-gray-100 text-gray-500">Inactif</Badge>}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-1">
          <Btn variant="ghost" size="sm" onClick={() => onEdit(role)}><Pencil size={13} /></Btn>
          <Btn variant="ghost" size="sm" onClick={() => onDelete(role)} className="text-red-500 hover:text-red-600"><Trash2 size={13} /></Btn>
        </div>
      </td>
    </tr>
  );
}
